import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Aviso, Bandeja, Canal, NOMBRE_CANAL, Preferencias, TipoPref, abrirEnlace, avisosApi, cuando, errorTexto, horaAmPm } from "./avisos.service";
import { ponerConteo } from "./conteoAvisos";
import "./avisos.css";

/**
 * Pantalla "Avisos" (todas las personas, Esteban 2026-10-10): la bandeja con filtros por grupo y, en la otra pestaña,
 * "Mis Avisos": por dónde y cuándo le llega cada aviso, las horas del resumen y el silencio nocturno.
 */
const AvisosView: React.FC = () => {
	const params = new URLSearchParams(useLocation().search);
	const [vista, setVista] = useState<"bandeja" | "preferencias">(params.get("vista") === "preferencias" ? "preferencias" : "bandeja");
	const navigate = useNavigate();

	return (
		<div className="avisos-pag">
			<div className="av-cab">
				<h1>Avisos</h1>
				<div className="av-pestanas" role="tablist">
					<button role="tab" aria-selected={vista === "bandeja"} className={vista === "bandeja" ? "on" : ""}
						onClick={() => { setVista("bandeja"); navigate("/avisos", { replace: true }); }}>Bandeja</button>
					<button role="tab" aria-selected={vista === "preferencias"} className={vista === "preferencias" ? "on" : ""}
						onClick={() => { setVista("preferencias"); navigate("/avisos?vista=preferencias", { replace: true }); }}>Mis Avisos</button>
				</div>
			</div>
			{vista === "bandeja" ? <BandejaView grupoInicial={params.get("grupo")} /> : <MisAvisos />}
		</div>
	);
};

// ====================================================================== bandeja

const GRUPOS = ["Pagos", "Facturas", "Matrículas", "Horarios", "Asistencias", "Caja", "Ventas", "WhatsApp", "Sistema"];

const BandejaView: React.FC<{ grupoInicial: string | null }> = ({ grupoInicial }) => {
	const navigate = useNavigate();
	const [grupo, setGrupo] = useState<string | null>(grupoInicial);
	const [soloNuevos, setSoloNuevos] = useState(false);
	const [datos, setDatos] = useState<Bandeja | null>(null);
	const [mas, setMas] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const cargar = (antes?: number) => {
		setError(null);
		avisosApi.bandeja({ grupo: grupo || undefined, no_leidos: soloNuevos, antes }).then((r) => {
			ponerConteo(r.data.no_leidos);
			setDatos((d) => (antes && d ? { ...r.data, avisos: [...d.avisos, ...r.data.avisos] } : r.data));
		}).catch((e) => setError(errorTexto(e))).finally(() => setMas(false));
	};
	useEffect(() => { setDatos(null); cargar(); }, [grupo, soloNuevos]);

	const marcar = async (a: Aviso, leido: boolean) => {
		try {
			ponerConteo((await avisosApi.leer({ ids: [a.id], leido })).data.no_leidos);
			setDatos((d) => d && { ...d, avisos: d.avisos.map((x) => (x.id === a.id ? { ...x, leido } : x)) });
		} catch (e) { setError(errorTexto(e)); }
	};
	const abrir = (a: Aviso) => {
		if (!a.leido) marcar(a, true);
		abrirEnlace(a.enlace, navigate);
	};
	const leerTodo = async () => {
		try {
			ponerConteo((await avisosApi.leer({ todos: true, grupo: grupo || undefined })).data.no_leidos);
			cargar();
		} catch (e) { setError(errorTexto(e)); }
	};

	const total = datos?.no_leidos || 0;
	return (
		<>
			<div className="av-filtros">
				<div className="av-chips">
					<button className={!grupo ? "on" : ""} onClick={() => setGrupo(null)}>Todos{total ? <i>{total}</i> : null}</button>
					{GRUPOS.filter((g) => (datos?.por_grupo[g] || 0) > 0 || grupo === g).map((g) => {
						const n = datos?.por_grupo[g] || 0;
						return <button key={g} className={grupo === g ? "on" : ""} onClick={() => setGrupo(g)}>{g}{n ? <i>{n}</i> : null}</button>;
					})}
				</div>
				<div className="av-acciones">
					<label className="av-check"><input type="checkbox" checked={soloNuevos} onChange={(e) => setSoloNuevos(e.target.checked)} /> Solo Sin Leer</label>
					<button className="av-btn" disabled={!total} onClick={leerTodo}>Marcar Todo como Leído</button>
				</div>
			</div>
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			{!datos ? <div className="loader" /> : datos.avisos.length === 0 ? (
				<div className="av-vacio">{soloNuevos ? "Nada sin leer." : "No tienes avisos todavía."}</div>
			) : (
				<div className="av-lista">
					{datos.avisos.map((a) => (
						<div key={a.id} className={"av-item" + (a.leido ? "" : " nuevo") + (a.prioridad === "urgente" ? " urgente" : "")}>
							<button className="av-item-cuerpo" onClick={() => abrir(a)}>
								<span className="avc-punto" aria-hidden="true" />
								<span className="av-item-txt">
									<span className="av-item-tit"><b>{a.titulo}</b>{a.prioridad === "urgente" ? <em>Urgente</em> : null}</span>
									{a.cuerpo ? <span className="av-item-desc">{a.cuerpo}</span> : null}
									<small>{a.tipo} · {a.grupo}{a.plantel ? " · " + a.plantel : ""} · {cuando(a.creado)}</small>
								</span>
							</button>
							<button className="av-item-marca" title={a.leido ? "Marcar como No Leído" : "Marcar como Leído"}
								aria-label={a.leido ? "Marcar como no leído" : "Marcar como leído"} onClick={() => marcar(a, !a.leido)}>
								{a.leido ? "○" : "●"}
							</button>
						</div>
					))}
					{datos.hay_mas ? (
						<button className="av-btn av-mas" disabled={mas} onClick={() => { setMas(true); cargar(datos.avisos[datos.avisos.length - 1].id); }}>
							{mas ? "Cargando…" : "Ver Más"}
						</button>
					) : null}
				</div>
			)}
		</>
	);
};

// ====================================================================== Mis Avisos

const CANALES: Canal[] = ["wapp", "push", "wappteam", "correo"];

const MisAvisos: React.FC = () => {
	const [p, setP] = useState<Preferencias | null>(null);
	const [todos, setTodos] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState<number | null>(null);
	const [horas, setHoras] = useState<string[]>([]);
	const [silencio, setSilencio] = useState<{ desde: string; hasta: string } | null>(null);

	const aplicar = (d: Preferencias) => { setP(d); setHoras(d.general.resumen); setSilencio(d.general.silencio); };
	useEffect(() => { avisosApi.preferencias().then((r) => aplicar(r.data)).catch((e) => setError(errorTexto(e))); }, []);

	const guardar = async (t: TipoPref, cambio: Partial<{ canales: Canal[]; modo: string; apagado: boolean; restablecer: boolean }>) => {
		setOcupado(t.id); setError(null);
		try {
			aplicar((await avisosApi.guardarPreferencia({ tipo_id: t.id, canales: t.canales, modo: t.modo, apagado: t.apagado, ...cambio })).data);
		} catch (e) { setError(errorTexto(e)); } finally { setOcupado(null); }
	};
	const guardarGeneral = async () => {
		setError(null); setOk(null);
		try { aplicar((await avisosApi.guardarGeneral({ resumen: horas, silencio })).data); setOk("Guardado."); }
		catch (e) { setError(errorTexto(e)); }
	};

	const porGrupo = useMemo(() => {
		const m: Record<string, TipoPref[]> = {};
		(p?.tipos || []).filter((t) => todos || t.te_llega !== "no").forEach((t) => { (m[t.grupo] = m[t.grupo] || []).push(t); });
		return m;
	}, [p, todos]);

	if (!p) return error ? <div className="av-aviso oro">{error}</div> : <div className="loader" />;
	const cambioGeneral = JSON.stringify(horas) !== JSON.stringify(p.general.resumen) || JSON.stringify(silencio) !== JSON.stringify(p.general.silencio);

	return (
		<>
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			<section className="av-tarjeta">
				<h2>Resumen por Correo y Silencio</h2>
				<div className="av-general">
					<div className="av-campo">
						<span className="av-etq">Horas del resumen</span>
						<div className="av-horas">
							{horas.map((h, i) => (
								<span key={i} className="av-hora">
									<input type="time" value={h} onChange={(e) => setHoras(horas.map((x, j) => (j === i ? e.target.value : x)))} aria-label={"Hora " + (i + 1)} />
									{horas.length > 1 ? <button className="av-x" aria-label="Quitar hora" onClick={() => setHoras(horas.filter((_, j) => j !== i))}>×</button> : null}
								</span>
							))}
							{horas.length < 4 ? <button className="av-btn chico" onClick={() => setHoras([...horas, "12:00"])}>Agregar Hora</button> : null}
						</div>
						<small>{horas.map(horaAmPm).join(" y ")} · hora de {p.general.zona.split("/")[1]?.replace("_", " ")}</small>
					</div>
					<div className="av-campo">
						<label className="av-check">
							<input type="checkbox" checked={!!silencio} onChange={(e) => setSilencio(e.target.checked ? { desde: "21:00", hasta: "07:00" } : null)} />
							Silencio nocturno
						</label>
						{silencio ? (
							<div className="av-horas">
								<input type="time" value={silencio.desde} onChange={(e) => setSilencio({ ...silencio, desde: e.target.value })} aria-label="Desde" />
								<span>a</span>
								<input type="time" value={silencio.hasta} onChange={(e) => setSilencio({ ...silencio, hasta: e.target.value })} aria-label="Hasta" />
							</div>
						) : null}
						<small>{silencio ? "Sin celular ni WappTeam; lo urgente sí llega." : "Apagado."}</small>
					</div>
					<div className="av-campo fin">
						<button className="av-btn pri" disabled={!cambioGeneral} onClick={guardarGeneral}>Guardar</button>
						{ok && !cambioGeneral ? <small>{ok}</small> : null}
					</div>
				</div>
			</section>

			<div className="av-filtros">
				<label className="av-check"><input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} /> Ver Todos los Avisos</label>
			</div>

			{Object.keys(porGrupo).length === 0 ? <div className="av-vacio">Hoy no te llega ningún aviso.</div> : Object.entries(porGrupo).map(([g, tipos]) => (
				<section key={g} className="av-tarjeta">
					<h2>{g}</h2>
					<div className="av-prefs">
						{tipos.map((t) => {
							const urgente = t.prioridad === "urgente";
							return (
								<div key={t.id} className={"av-pref" + (t.apagado ? " apagado" : "") + (ocupado === t.id ? " ocupado" : "")}>
									<div className="av-pref-nombre">
										<b>{t.nombre}{urgente ? <em>Urgente</em> : null}</b>
										<small>{t.te_llega === "siempre" ? "Te llega" : t.te_llega === "cuando_participas" ? "Cuando participas" : "No te llega hoy"}
											{t.personal ? " · cambiado por ti" : ""}</small>
									</div>
									<div className="av-pref-canales" role="group" aria-label={"Canales de " + t.nombre}>
										{CANALES.map((c) => {
											const permitido = t.permitidos.includes(c);
											const fijo = (c === "wapp" && t.bandeja) || (urgente && (c === "wapp" || c === "push"));
											const on = t.canales.includes(c);
											return (
												<button key={c} disabled={!permitido || fijo || t.apagado || ocupado === t.id} aria-pressed={on}
													className={"av-canal" + (on ? " on" : "") + (!permitido ? " no" : "")}
													title={!permitido ? "No disponible para este aviso" : fijo ? "Siempre" : ""}
													onClick={() => guardar(t, { canales: on ? t.canales.filter((x) => x !== c) : [...t.canales, c] })}>
													{NOMBRE_CANAL[c]}
												</button>
											);
										})}
									</div>
									<div className="av-pref-modo">
										{t.bandeja ? (
											<select value={t.modo} disabled={urgente || t.apagado || ocupado === t.id} onChange={(e) => guardar(t, { modo: e.target.value })}
												aria-label={"Cuándo llega " + t.nombre}>
												<option value="inmediato">Al Momento</option>
												<option value="resumen">En el Resumen</option>
											</select>
										) : <span className="av-tenue">Al momento</span>}
										<label className="av-switch" title={urgente ? "Lo urgente no se apaga" : ""}>
											<input type="checkbox" checked={!t.apagado} disabled={urgente || ocupado === t.id} onChange={(e) => guardar(t, { apagado: !e.target.checked })} />
											<span>{t.apagado ? "Apagado" : "Prendido"}</span>
										</label>
										{t.personal ? <button className="av-link" onClick={() => guardar(t, { restablecer: true })}>Restablecer</button> : null}
									</div>
								</div>
							);
						})}
					</div>
				</section>
			))}
		</>
	);
};

export default AvisosView;
